// APP-6502 (FR-006a): derive the user's last-used recorder settings and map them
// into the offline create args. `deriveRecorderSettingsFromUser` feeds the online
// snapshot writer (`useCacheRecorderSettings`); `buildOfflineCreateArgs` runs in the
// offline create mutation, preferring the live user object and falling back to the
// persisted snapshot on a cold offline boot.
import {
  getCachedLastUsedRecorderSettings,
  type CachedRecorderSettings,
} from '@/atoms/offline-recording/cached-recorder-settings';
import { getPendingUserDefaultTranscribeMode } from '@/atoms/offline-recording/pending-user-default-transcribe-mode';
import { getTauriLastUsedInputDevice } from '@/atoms/persistedPreferencesAtoms';
import {
  getCurrentUser,
  getDefaultDictationInputLanguages,
  getDefaultDictationOutputLanguages,
} from '@/hooks/query/useQueryUser';
import type { CreateOfflineRecordingArgs } from '@/tauri/offline-recording/recording';
import { DEFAULT_TEMPLATE_AUTO_MODE } from '@heidi/session/contracts';
import { toRecordableMode } from '@heidi/session/data';

/**
 * Snapshot the FR-006a basic settings from the currently-cached user object.
 * Returns `null` when there is no cached user (e.g. cold offline boot) — the caller
 * then falls back to the persisted snapshot. "Last-used" is the user's persisted
 * defaults, which the online recorder keeps current as the user changes settings.
 */
export function deriveRecorderSettingsFromUser(): CachedRecorderSettings | null {
  const user = getCurrentUser();
  if (!user) {
    return null;
  }

  const settings = user.session_settings;
  const output = getDefaultDictationOutputLanguages();

  // The AUTO_MODE sentinel ('Auto') is not a real template id. The online path
  // (scribeRecordModel.ts) special-cases it as the AUTO_MODE generation flow rather
  // than a literal templateId. The offline create args carry no generationMethod
  // field, so the faithful equivalent is to omit templateId — the backend then
  // applies its auto default on sync instead of receiving a bogus 'AUTO_MODE' id.
  const defaultTemplateId = settings?.default_template_id;

  return {
    templateId:
      defaultTemplateId && defaultTemplateId !== DEFAULT_TEMPLATE_AUTO_MODE
        ? defaultTemplateId
        : undefined,
    transcribeMode: toRecordableMode(settings?.last_recording_mode) ?? undefined,
    inputLanguage: getDefaultDictationInputLanguages()?.[0],
    // `getDefaultDictationOutputLanguages()` bakes in an `'en'` fallback on every path,
    // so this is never actually `undefined` — an unset user gets `'en'`, matching the
    // online recorder (not the server default). See the `outputLanguage` field doc.
    outputLanguage: output.outputLanguages?.[0] ?? output.outputLanguageCode ?? undefined,
    voiceStyle: settings?.default_voice_style ?? undefined,
  };
}

/**
 * The user's current recorder defaults: the live user object, falling back to
 * the persisted snapshot on a cold offline boot (where `getCurrentUser()` is
 * empty because nothing has fetched `/user` yet).
 *
 * Exported because two callers now need the same resolution and must not
 * diverge: the create args below, and the offline-local session stub that
 * *displays* the template and note style before a recording starts.
 *
 * **`transcribeMode` is overlaid from the offline pending default.**
 * Both sources under it report `session_settings.last_recording_mode`, which is
 * server state — and a mode chosen while offline never reaches the server,
 * because the `/user` PATCH is skipped offline by the offline read-only
 * policy. So both
 * sources are stale by construction for exactly this field, and the pending
 * value is the fresher one until the reconnect replay lands. The overlay is
 * applied here rather than at each call site so the create args and the stub
 * that displays them cannot disagree about which mode the session is in.
 *
 * Only `transcribeMode` is overlaid. The other fields have no equivalent
 * offline-deferred write: their per-session edits go through
 * `offline_recording_update_settings` onto the session's own row, and their
 * account defaults are resolved at sync time by the engine.
 */
export function resolveLastUsedRecorderSettings(): CachedRecorderSettings | null {
  const resolved =
    deriveRecorderSettingsFromUser() ?? getCachedLastUsedRecorderSettings();
  const pendingMode = getPendingUserDefaultTranscribeMode();
  if (!pendingMode) {
    return resolved;
  }
  // Survives `resolved === null` deliberately: on a cold offline boot with no
  // cached user AND no snapshot, the mode the clinician picked before quitting
  // is the only recorder setting we know, and losing it here is the offline
  // relaunch reset all over again.
  return { ...resolved, transcribeMode: toRecordableMode(pendingMode) ?? undefined };
}

/**
 * Map the last-used settings (live user, else the persisted snapshot) plus the
 * already-persisted last-used microphone into the offline create args.
 *
 * `patientLink` is deliberately omitted — it's APP-6501. The advanced style knobs
 * (Detail/bullets/quotes/abbreviations) and the Free/Pro brain are out of scope
 * offline per FR-006a; greying them out is APP-6672.
 *
 * **`templateId` and `noteStyle` are deliberately NOT sent (DSK-873.)** Writing
 * them here snapshots the user's defaults at *create*, and nothing re-read them
 * afterwards — so a clinician who changed their default template between
 * opening the session and pressing record got the stale one.
 *
 * The fix is to send nothing rather than to re-resolve later, because the sync
 * engine already resolves these two the right way. `note_generation_params_from_session`
 * maps a NULL column to `None`, and `note_params_from_defaults` merges the
 * session's params *over* `resolve_note_defaults()` **per field** — so a NULL
 * template picks up `session_settings.default_template_id` fetched at sync
 * time, which is fresher than anything the client could have captured. A
 * non-NULL column, by contrast, *wins* that merge: the create-time snapshot was
 * actively overriding the correct value.
 *
 * That also buys the provenance signal this ticket otherwise had no way to get.
 * With create writing NULL, the only thing that can make these columns non-NULL
 * is `offline_recording_update_settings`, which only the per-session pickers
 * call. So "non-NULL" now means exactly "the clinician chose this for this
 * session", and it correctly still beats the account default.
 *
 * `dictationType` stays: `note_params_from_defaults` builds its defaults with
 * `transcribe_mode: None` and only ever takes that field from the session's own
 * params, so there is no account default for the engine to fall back to.
 * Dropping it would lose the mode outright rather than refresh it.
 * `language` / `outputLanguage` stay because no consumer reads them for note
 * generation at all — `offline_recording_list` reads them back purely to
 * rehydrate the pickers after a relaunch.
 */
export function buildOfflineCreateArgs(): CreateOfflineRecordingArgs {
  const snapshot = resolveLastUsedRecorderSettings();
  const microphone = getTauriLastUsedInputDevice();

  return {
    // The create arg is named `dictationType`, but it carries the transcribe-vs-dictate
    // mode (`TranscribeMode`) — the online recorder's green toggle, not the separate
    // in-note dictation feature.
    dictationType: snapshot?.transcribeMode,
    language: snapshot?.inputLanguage,
    outputLanguage: snapshot?.outputLanguage,
    microphone: microphone ?? undefined,
  };
}
